/**
 * Format a byte size to a human-readable string
 */
export function formatByteSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))

  // Round to 2 decimal places
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + units[i]
}

/**
 * Format time in seconds to a human-readable string
 */
export function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  } else {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
}

/**
 * Format text with newlines for HTML display
 */
export function formatTextWithNewlines(text: string): string {
  return text.replace(/\n/g, '<br>')
}

/**
 * Calculate the number of days remaining until a deletion date
 * @param deletionScheduledAt ISO date string of when deletion is scheduled
 * @returns Number of days remaining (rounded up), or 0 if past due
 */
export function getDaysUntilDeletion(deletionScheduledAt: string | null): number {
  if (!deletionScheduledAt) return 0

  const deletionDate = new Date(deletionScheduledAt)
  const now = new Date()

  // Calculate the difference in milliseconds
  const timeDifference = deletionDate.getTime() - now.getTime()

  // Convert to days and round up
  const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))

  // Return 0 if the date has passed
  return Math.max(0, daysRemaining)
}

/**
 * Format a collection distance (in metres) to a compact string.
 * Under 100 km: one decimal place. 100 km and over: no decimal.
 */
export function formatCollectionDistance(metres: number): string {
  const km = metres / 1000
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`
}

/**
 * Format a collection duration (in seconds) to a compact display string.
 * Omits minutes when hours reach triple digits to save space.
 */
export function formatCollectionDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}m`
  if (h >= 100 || m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/**
 * Format a collection duration (in seconds) to an exact display string (always includes minutes).
 * Used for tooltips where space is not a concern.
 */
export function exactCollectionDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
